/**
 * Graph-extension removal: mutate a `GraphExtension` to drop a
 * set of graph-extension kinds with cascading edge / ontology cleanup.
 *
 * Pure function — no I/O, no compilation. The caller (`store.removeKinds`)
 * handles persistence (CAS commit of the resulting document) and the
 * deferred data-cleanup phase via `materializeRemovals`. This module
 * answers two questions:
 *
 *   1. Is it valid to remove these names? Compile-time kinds are
 *      rejected (compile-time kinds are removed by recompiling and
 *      redeploying — persisting "removed-compile-time-kind" state in
 *      `schema_doc` is incoherent). Graph-extension kinds referenced by a
 *      compile-time edge or ontology relation are also rejected
 *      (removing them would orphan compile-time references at the
 *      next deploy).
 *
 *   2. What does the new document look like? Edges whose endpoint
 *      list becomes empty after removal are cascading-removed; edges
 *      with surviving endpoints are retained with the removed name
 *      dropped from `from`/`to`. Ontology relations referencing any
 *      removed kind are also cascading-removed.
 */
import { type GraphDef } from "../core/define-graph";
import { projectTargetKinds } from "../core/edge-endpoints";
import { getTypeName } from "../ontology/types";
import { createDataKeyedBag } from "../utils/object";
import {
  KindHasReferentsError,
  type KindReferent,
  RemoveCompileTimeKindError,
} from "./errors";
import {
  type ExtensionEdgeDef,
  type ExtensionIndex,
  type ExtensionNodeDef,
  type ExtensionOntologyRelation,
  type GraphExtension,
} from "./extension-types";
import {
  buildGraphExtensionOntologyKeySet,
  compileTimeOntologyKey,
  extensionKindNames,
} from "./ontology-keys";

type RemovalPlan = Readonly<{
  /**
   * The new extension after applying the removals. When the input
   * list contained only absent names, the document is structurally
   * equal to the existing extension and the caller short-circuits
   * the schema commit.
   */
  document: GraphExtension | undefined;
  /** Kinds actually removed (extension kinds present in the document). */
  removedNodeKinds: readonly string[];
  /** Edge kinds dropped because every endpoint they connected was removed. */
  removedEdgeKinds: readonly string[];
}>;

/**
 * Plans the removal of a set of names from a host graph's
 * extension, validating against the host graph's compile-time
 * kinds and graph-extension kinds. Throws on the rejection cases:
 *
 *   - `RemoveCompileTimeKindError` if any name is a compile-time kind.
 *   - `KindHasReferentsError` if a graph-extension kind is referenced
 *     by a compile-time edge endpoint or ontology relation.
 *
 * Returns a plan describing the new document and which kinds will be
 * cascading-removed alongside the explicitly named ones.
 */
export function planRemovals<G extends GraphDef>(
  graph: G,
  names: readonly string[],
): RemovalPlan {
  const document = graph.extension;
  const namesSet = new Set(names);
  if (namesSet.size === 0) {
    return { document, removedNodeKinds: [], removedEdgeKinds: [] };
  }

  const { nodes: runtimeNodeNames, edges: runtimeEdgeNames } =
    extensionKindNames(document);
  const compileTimeNodeNames = new Set(
    Object.keys(graph.nodes).filter((name) => !runtimeNodeNames.has(name)),
  );
  const compileTimeEdgeNames = new Set(
    Object.keys(graph.edges).filter((name) => !runtimeEdgeNames.has(name)),
  );

  // Reject compile-time names first — same shape across the API
  // surface even when the call also references absent runtime names.
  for (const name of namesSet) {
    if (compileTimeNodeNames.has(name)) {
      throw new RemoveCompileTimeKindError(name, "node", graph.id);
    }
    if (compileTimeEdgeNames.has(name)) {
      throw new RemoveCompileTimeKindError(name, "edge", graph.id);
    }
  }

  const removedNodeKinds = [...namesSet].filter((name) =>
    runtimeNodeNames.has(name),
  );
  const explicitlyRemovedEdgeKinds = [...namesSet].filter((name) =>
    runtimeEdgeNames.has(name),
  );

  // Compile-time-edge referent check: a graph-extension kind being
  // removed cannot remain a target of any compile-time edge or ontology
  // relation, because the compile-time declaration would resurrect the
  // reference on the next deploy. Pre-build an inverted index of
  // `kindName → referents` once so the per-kind check is O(1) instead
  // of walking all edges + ontology per removed kind.
  const referentsByKind = buildCompileTimeReferentIndex(
    graph,
    runtimeEdgeNames,
  );
  for (const kindName of removedNodeKinds) {
    const referents = referentsByKind.get(kindName);
    if (referents !== undefined && referents.length > 0) {
      throw new KindHasReferentsError(kindName, referents, graph.id);
    }
  }

  if (document === undefined) {
    return {
      document: undefined,
      removedNodeKinds: [],
      removedEdgeKinds: [],
    };
  }

  // Build the cascading-edges set: any graph-extension edge whose `from` or
  // `to` becomes empty after removal is also removed; edges with
  // surviving endpoints are retained with the removed name pruned.
  const removedNodeKindsSet = new Set(removedNodeKinds);
  const explicitlyRemovedEdgeKindsSet = new Set(explicitlyRemovedEdgeKinds);
  const cascadeEdges = new Set<string>();
  // Data-keyed: edge kind names read out of the persisted document.
  const updatedEdges = createDataKeyedBag<ExtensionEdgeDef>();

  for (const [edgeName, edgeDocument] of Object.entries(document.edges ?? {})) {
    if (explicitlyRemovedEdgeKindsSet.has(edgeName)) {
      cascadeEdges.add(edgeName);
      continue;
    }
    let newFrom = edgeDocument.from.filter(
      (kind) => !removedNodeKindsSet.has(kind),
    );
    const edgeTo = edgeDocument.to;
    let newTo: readonly string[] | Record<string, readonly string[]>;
    if (Array.isArray(edgeTo)) {
      newTo = (edgeTo as readonly string[]).filter(
        (kind: string): boolean => !removedNodeKindsSet.has(kind),
      );
      if (newFrom.length === 0 || newTo.length === 0) {
        cascadeEdges.add(edgeName);
        continue;
      }
    } else {
      const updatedMap = createDataKeyedBag<readonly string[]>();
      const targetMap = edgeTo as Readonly<Record<string, readonly string[]>>;
      const survivingFrom: string[] = [];
      for (const sourceKind of newFrom) {
        const targets = (targetMap[sourceKind] ?? []).filter(
          (kind: string): boolean => !removedNodeKindsSet.has(kind),
        );
        if (targets.length > 0) {
          survivingFrom.push(sourceKind);
          updatedMap[sourceKind] = targets;
        }
      }
      if (survivingFrom.length === 0) {
        cascadeEdges.add(edgeName);
        continue;
      }
      newFrom = survivingFrom;
      newTo = updatedMap;
    }
    updatedEdges[edgeName] = {
      ...edgeDocument,
      from: newFrom,
      to: newTo,
    };
  }

  // Filter nodes — explicit removals are dropped.
  // Data-keyed: node kind names read out of the persisted document.
  const updatedNodes = createDataKeyedBag<ExtensionNodeDef>();
  for (const [nodeName, nodeDocument] of Object.entries(document.nodes ?? {})) {
    if (removedNodeKindsSet.has(nodeName)) continue;
    updatedNodes[nodeName] = nodeDocument;
  }

  // Drop ontology relations referencing any removed kind. The compile-
  // time-referent check above already rejects compile-time-side
  // ontology referencing the removed kind, so any remaining ontology
  // is safe to filter.
  const survivingOntology: ExtensionOntologyRelation[] = (
    document.ontology ?? []
  ).filter(
    (relation) =>
      !removedNodeKindsSet.has(relation.from) &&
      !removedNodeKindsSet.has(relation.to),
  );

  // Drop graph-extension indexes referencing removed kinds (relational + edge
  // entries; the auto-derived vector indexes follow the graph-extension nodes
  // they were derived from, so dropping the graph-extension node implicitly
  // drops the index when `mergeGraphExtension` re-runs auto-derive
  // against the smaller node set).
  const allRemovedKinds = new Set([
    ...removedNodeKindsSet,
    ...cascadeEdges,
    ...explicitlyRemovedEdgeKindsSet,
  ]);
  const survivingIndexes: ExtensionIndex[] = (document.indexes ?? []).filter(
    (index) => !allRemovedKinds.has(index.kind),
  );

  const newDocument: GraphExtension = Object.freeze({
    ...(document.version === undefined ? {} : { version: document.version }),
    // Spread at the boundary: these land on the returned extension document,
    // which is public as `graph.extension`. See `createDataKeyedBag` in
    // ../utils/object.ts.
    ...(Object.keys(updatedNodes).length === 0 ?
      {}
    : { nodes: { ...updatedNodes } }),
    ...(Object.keys(updatedEdges).length === 0 ?
      {}
    : { edges: { ...updatedEdges } }),
    ...(survivingOntology.length === 0 ? {} : { ontology: survivingOntology }),
    ...(survivingIndexes.length === 0 ? {} : { indexes: survivingIndexes }),
  });

  return {
    document: newDocument,
    removedNodeKinds,
    removedEdgeKinds: [
      ...explicitlyRemovedEdgeKinds,
      ...[...cascadeEdges].filter(
        (name) => !explicitlyRemovedEdgeKindsSet.has(name),
      ),
    ],
  };
}

/**
 * Returns a graph with the graph-extension slice stripped: nodes and edges
 * registered by `mergeGraphExtension` are dropped along with the
 * `extension` itself, leaving the compile-time-only graph.
 *
 * Used by `removeKinds` as the base for re-merging the post-removal
 * graph-extension document — the alternative (mutating `graph.nodes` /
 * `graph.edges` in-place) would skip the merge step's validation
 * (collision checks, endpoint resolution).
 */
export function stripGraphExtension<G extends GraphDef>(graph: G): G {
  const document = graph.extension;
  if (document === undefined) return graph;
  const { nodes: runtimeNodeNames, edges: runtimeEdgeNames } =
    extensionKindNames(document);
  const runtimeOntologyKeys = buildGraphExtensionOntologyKeySet(document);

  // Data-keyed: registered node kind names.
  const compileNodes = createDataKeyedBag<(typeof graph.nodes)[string]>();
  for (const [name, registration] of Object.entries(graph.nodes)) {
    if (runtimeNodeNames.has(name)) continue;
    compileNodes[name] = registration;
  }
  // Data-keyed: registered edge kind names.
  const compileEdges = createDataKeyedBag<(typeof graph.edges)[string]>();
  for (const [name, registration] of Object.entries(graph.edges)) {
    if (runtimeEdgeNames.has(name)) continue;
    compileEdges[name] = registration;
  }
  const compileOntology = graph.ontology.filter(
    (relation) => !runtimeOntologyKeys.has(compileTimeOntologyKey(relation)),
  );
  // Drop runtime-origin indexes too — `mergeGraphExtension` re-runs
  // auto-derivation against the new (smaller) graph-extension kind set.
  const compileIndexes = (graph.indexes ?? []).filter(
    (index) => index.origin !== "runtime",
  );

  // Spread at the boundary: these become `graph.nodes` / `graph.edges` on the
  // returned (public) `GraphDef`.
  return Object.freeze({
    ...graph,
    nodes: { ...compileNodes },
    edges: { ...compileEdges },
    ontology: compileOntology,
    indexes: compileIndexes.length === 0 ? undefined : compileIndexes,
    extension: undefined,
  });
}

/**
 * Builds an inverted index `kindName → KindReferent[]` over the
 * compile-time edges and ontology relations of `graph`. One walk per
 * graph instead of one walk per removed kind, dropping the cost of the
 * referent check from O(K × (E + O)) to O(K + E + O).
 */
function buildCompileTimeReferentIndex<G extends GraphDef>(
  graph: G,
  runtimeEdgeNames: ReadonlySet<string>,
): ReadonlyMap<string, readonly KindReferent[]> {
  const index = new Map<string, KindReferent[]>();
  const append = (kindName: string, referent: KindReferent): void => {
    const existing = index.get(kindName);
    if (existing === undefined) index.set(kindName, [referent]);
    else existing.push(referent);
  };

  for (const [edgeName, registration] of Object.entries(graph.edges)) {
    if (runtimeEdgeNames.has(edgeName)) continue;
    const referent: KindReferent = {
      type: "compile-time-edge",
      name: edgeName,
    };
    const seen = new Set<string>();
    for (const endpoint of registration.from) {
      if (seen.has(endpoint.kind)) continue;
      seen.add(endpoint.kind);
      append(endpoint.kind, referent);
    }
    for (const toKind of projectTargetKinds(registration.to)) {
      if (seen.has(toKind)) continue;
      seen.add(toKind);
      append(toKind, referent);
    }
  }

  const runtimeOntologyKeys = buildGraphExtensionOntologyKeySet(
    graph.extension,
  );
  for (const relation of graph.ontology) {
    if (runtimeOntologyKeys.has(compileTimeOntologyKey(relation))) continue;
    const fromName = getTypeName(relation.from);
    const toName = getTypeName(relation.to);
    const referent: KindReferent = {
      type: "compile-time-ontology",
      name: `${relation.metaEdge.name}(${fromName}, ${toName})`,
    };
    append(fromName, referent);
    if (toName !== fromName) append(toName, referent);
  }

  return index;
}
